import usePageTitle from "@/hooks/usePageTitle";
import { useNavigate, Path } from "@/router";
import { useParams, useSearchParams } from "react-router-dom";
import usePeopleQuery from "@/pages/people/_hooks/usePeopleQuery";
import PeopleTable from "@/pages/people/_components/PeopleTable";
import useTableRowMenu from "@/hooks/useTableRowMenu";
import { useEffect, useState } from "react";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import PersonSideSheet from "@/features/person/components/PersonSideSheet/PersonSideSheet";
import { Query } from "@/pages/people/_hooks/useSearchParams";
import { SectionDetailsHeader } from "../_components/SectionDetailsHeader";
import { SectionPeopleTabs } from "../_components/SectionPeopleTabs";
import { useQuery } from "@apollo/client";
import { SECTION_DETAILS_HEADER_QUERY } from "../queries";
import { SECTION_PEOPLE_TABLE_PATH } from "../_utils";

export default function SectionPeoplePage() {
  useRequireCapability({ domain: DOMAIN.SECTIONS, permission: PERMISSION.viewPeople });
  const { sectionId } = useParams();
  const navigate = useNavigate();
  const { data: sectionData } = useQuery(SECTION_DETAILS_HEADER_QUERY, {
    variables: { sectionId },
    skip: !sectionId,
    fetchPolicy: "cache-first"
  });
  const sectionName = sectionData?.section?.name ?? "Section";
  usePageTitle(SECTION_PEOPLE_TABLE_PATH, `Sections | Section Details | People in Section | ${sectionName}`);

  const [searchParams] = useSearchParams();
  const [advancedSearchQuery, setAdvancedSearchQuery] = useState<Partial<Query>>({});

  useEffect(() => {
    const query: Partial<Query> = {};
    const fields = ["email", "phone", "contactPostalCode"] as const;

    fields.forEach((field) => {
      const value = searchParams.get(field);
      if (value) {
        query[field] = value;
      }
    });

    setAdvancedSearchQuery(query);
  }, [searchParams]);

  const tableProps = usePeopleQuery({
    ...advancedSearchQuery,
    sectionIds: sectionId ? [sectionId] : []
  });
  const { Menu, setMenu } = useTableRowMenu();
  const [isPersonSideSheetOpen, setIsPersonSideSheetOpen] = useState(false);
  const [selectedPgaId, setSelectedPgaId] = useState<string | undefined>(undefined);

  const returnTo = sectionId ? `/sections/${sectionId}/people/section-people` : "/people";

  if (!sectionId) {
    return null;
  }

  return (
    <>
      <Menu />
      <SectionDetailsHeader sectionId={sectionId} />
      <SectionPeopleTabs />
      <div className="w-full">
        <div className="pt-12" />
        <PeopleTable
          {...tableProps}
          setMenu={setMenu}
          advancedSearchQuery={advancedSearchQuery}
          lockedSectionId={sectionId}
          personReturnTo={returnTo}
          onViewPerson={(pgaId: string) => {
            setSelectedPgaId(pgaId);
            setIsPersonSideSheetOpen(true);
          }}
        />
      </div>
      <PersonSideSheet
        open={isPersonSideSheetOpen}
        onClose={() => {
          setIsPersonSideSheetOpen(false);
          setSelectedPgaId(undefined);
        }}
        pgaId={selectedPgaId ?? null}
        showEmployment={true}
        showFacility={true}
        customActionButtons={
          selectedPgaId
            ? [
                {
                  text: "View Person",
                  variant: "outlined" as const,
                  onClick: () => {
                    navigate(
                      `/people/${selectedPgaId}/person/overview?return_to=${returnTo}&${tableProps.searchParamsUrl}` as Path
                    );
                  }
                }
              ]
            : undefined
        }
      />
    </>
  );
}
